import { ColumnDef, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { Link, useParams } from 'react-router-dom';
import Check from 'assets/icons/check';
import Across from 'assets/icons/Across';
import GarbageArrowLess from 'assets/icons/GarbageArrowLess';
import Garbage from 'assets/icons/GarbageArrow';
import { useMemo, useState } from 'react';
import { payoutTypes } from 'utils/helpers/lists/payoutTypes';
import { allUnits } from 'utils/helpers/lists/units';
import Tooltip from 'antd/es/tooltip';
import { ObjectVacancyFragment } from 'pages/vacancy/api/fragments/generated/ObjectVacancy.fragment';
import { PayoutType, PayoutTypeUnitEnum, RateTypeEnum } from 'generated/graphql';
import { useEditObjectVacancyMutation } from 'pages/vacancy/api/mutations/generated/EditObjectVacancy';
import dayjs from 'dayjs';

export const useFacilityVacanciesTable = (data: ObjectVacancyFragment[]) => {
  const { facilityId } = useParams();

  const [changeStatus, { loading }] = useEditObjectVacancyMutation();
  const vacancyTime = (time: string) => {
    return time.split(', ').length > 1 ? time.split(', ').map(el => <div key={el}>{el}</div>) : time;
  };
  function getTimePeriod(time: string) {
    const timestamp = dayjs(time, 'HH:mm:ss');

    if (!timestamp.isValid()) {
      return 'Невалидное время';
    }

    const hour = timestamp.hour();
    return hour >= 6 && hour < 18 ? 'День' : 'Ночь';
  }

  const columns: ColumnDef<ObjectVacancyFragment>[] = useMemo(() => {
    return [
      {
        id: 'position',
        header: 'Профессия',
        size: 200,
        minSize: 200,
        cell: ({ row: { original } }) => {
          return (
            <Link to={`/facilities/${facilityId}/vacancies/${original.id}`} className="link">
              {original.objectProfession?.hrmsProfession?.name}
            </Link>
          );
        },
      },
      {
        id: 'timeStart',
        header: 'Начало смены',
        size: 100,
        minSize: 100,
        cell: ({ row: { original } }) => vacancyTime(original.shiftStartTime),
      },
      {
        id: 'timeEnd',
        header: 'Конец смены',
        size: 100,
        minSize: 100,
        cell: ({ row: { original } }) => vacancyTime(original.shiftEndTime),
      },
      {
        id: 'period',
        header: 'День/Ночь',
        size: 180,
        cell: ({ row: { original } }) => getTimePeriod(original.shiftStartTime),
      },
      {
        id: 'section',
        header: 'Отдел',
        size: 180,
        cell: ({ row: { original } }) => original.departmentOrSection,
      },
      {
        id: 'payout',
        header: 'Тип оплаты',
        size: 130,
        minSize: 130,
        cell: ({ row: { original } }) =>
          payoutTypes.find(payoutType => payoutType.id === (original.payoutType as unknown as PayoutType))?.name,
      },
      {
        id: 'unit',
        header: 'Ед. измерения',
        size: 130,
        minSize: 130,
        cell: ({ row: { original } }) =>
          allUnits.find(unit => (original.payoutTypeUnit as unknown as PayoutTypeUnitEnum) === unit.id)?.name,
      },
      {
        id: 'rate',
        header: 'ставка, ₽',
        minSize: 115,
        cell: ({ row: { original } }) => {
          return original.rate != null ? `${original.rate}` : '';
        },
      },
      {
        id: 'averageOrRangeRate',
        header: 'Стоимость смены',
        size: 180,
        cell: ({ row: { original } }) => {
          if (!original.isPublishShiftRate) return '';
          return original?.rateType === RateTypeEnum.Range
            ? `${original.rangeRateMin} - ${original.rangeRateMax}`
            : original.averageRate;
        },
      },
      {
        id: 'status',
        header: 'Статус',
        size: 87,
        cell: ({ row: { original } }) =>
          original.isActive ? <Check className="text-smena_green-dark" /> : <Across className="text-smena_red" />,
      },
      {
        id: 'erpSourceId',
        header: 'ERP ID Источника',
        size: 130,
        cell: ({ row: { original } }) => original.erpSourceId,
      },
      {
        id: 'erpGroupId',
        header: 'ERP ID Группы',
        size: 130,
        cell: ({ row: { original } }) => original.erpGroupId,
      },
      {
        id: 'actions',
        header: 'Действия',
        size: 115,
        minSize: 115,
        cell: ({ row: { original } }) => {
          return (
            <Tooltip title={'Изменить статус активности'}>
              <button
                disabled={loading}
                onClick={() =>
                  changeStatus({
                    variables: { input: { objectVacancyId: original.id, isActive: original.isActive ? false : true } },
                    update: cache => {
                      cache.modify({
                        id: `ObjectVacancyEntity:${original.id}`,
                        fields: {
                          isActive(prev: boolean) {
                            return !prev;
                          },
                        },
                      });
                    },
                  })
                }
              >
                {original.isActive ? <GarbageArrowLess /> : <Garbage />}
              </button>
            </Tooltip>
          );
        },
      },
    ];
  }, [changeStatus, loading, facilityId]);
  const [columnOrder, setColumnOrder] = useState(columns.map(column => column.id as string));

  return useReactTable({
    data,
    state: {
      columnOrder,
    },
    onColumnOrderChange: setColumnOrder,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    columns,
    meta: {
      position: true,
    },
  });
};
